import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useCallback, useState, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../core/modules/global';
import { CREATE_AREA_DIALOG } from '../../../../core/constants/dialog';
import Spinner from '../../Spinner';
import { useCompanyCore } from '../../../../core/modules/company';
import { useBusinessCore } from '../../../../core/modules/business';
import { Business } from '../../../../core/interfaces/business.interface';
// No se necesitan imports de validación de comisión

const createSchema = () => {
  return yup.object().shape({
    name: yup
      .string()
      .required('Campo requerido'),
    acronym: yup
      .string()
      .test('len', '3 caracteres requeridos', (val) => val?.length === 3)
      .required('Campo requerido'),
    commission: yup
      .number()
      .typeError('Debe ser un número')
      .min(0.1, 'Debe ser mayor a 0')
      .max(100, 'La comisión no puede ser mayor a 100%')
      .required('Campo requerido'),
  });
};

interface CreateAreaDialogProps {
  selectedBusiness: Business | null;
}

const CreateAreaDialog: React.FC<CreateAreaDialogProps> = ({
  selectedBusiness,
}) => {
  const { dialogs, closeDialog } = useGlobalCore();
  const { postArea, getBusinesses } = useBusinessCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading] = useState(false);
  const [sinComision, setSinComision] = useState(false);

  const schema = useMemo(() => {
    return yup.object().shape({
      name: yup.string().required('Campo requerido'),
      acronym: yup
        .string()
        .test('len', '3 caracteres requeridos', (val) => val?.length === 3)
        .required('Campo requerido'),
      commission: sinComision
        ? yup.number().nullable()
        : yup
            .number()
            .typeError('Debe ser un número')
            .required('Campo requerido'),
    });
  }, [sinComision]);

  const { control, formState, handleSubmit, resetField, watch, reset } =
    useForm({
      mode: 'onBlur',
      resolver: yupResolver(schema),
      defaultValues: {
        name: '',
        acronym: '',
        commission: 0,
      },
    });

  // Resetear formulario cuando cambie la empresa seleccionada
  useEffect(() => {
    if (selectedBusiness) {
      reset({
        name: '',
        acronym: '',
        commission: 0,
      });
    }
  }, [selectedBusiness, reset]);
  const { isValid, errors } = formState;

  // Observar los valores del formulario
  const watchedValues = watch();

  // Verificar si todos los campos requeridos están llenos
  const requiredFields: (keyof typeof watchedValues)[] = sinComision
    ? ['name', 'acronym']
    : ['name', 'acronym', 'commission'];
  const allRequiredFieldsFilled = requiredFields.every((field) => {
    const value = watchedValues[field];
    return (
      value !== undefined && value !== null && value.toString().trim() !== ''
    );
  });

  // Limpiar el campo commission cuando se marca "sin comisión"
  useEffect(() => {
    if (sinComision) {
      resetField('commission');
    }
  }, [sinComision, resetField]);

  const resetForm = useCallback(() => {
    resetField('name');
    resetField('acronym');
    resetField('commission');
    closeDialog(CREATE_AREA_DIALOG);
  }, [closeDialog, resetField]);

  const onSubmit = useCallback(
    (data: any) => {
      if (companyId && selectedBusiness) {
        setLoading(true);

        const payload = {
          name: data.name,
          acronym: data.acronym,
          commission: sinComision ? 0 : Number(data.commission),
        };

        postArea(
          companyId,
          selectedBusiness.id,
          payload,
          () => {
            resetForm();
            getBusinesses(companyId);
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        );
      }
    },
    [companyId, selectedBusiness, postArea, resetForm, getBusinesses, sinComision]
  );

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px] md:min-w-[500px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(CREATE_AREA_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Crear Área
              </Typography>
            </div>

            {selectedBusiness && (
              <div className="mb-24 p-16 bg-gray-50 rounded-lg">
                <Typography variant="body1" className="text-gray-800 mb-8">
                  Empresa: <strong>{selectedBusiness.name}</strong>
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                  Creando nueva área para la empresa seleccionada
                </Typography>
              </div>
            )}

            <div className="w-full max-w-xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre del Área"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.name}
                          helperText={errors?.name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="acronym"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Sigla del Área"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.acronym}
                          helperText={errors?.acronym?.message}
                          required
                          fullWidth
                          inputProps={{ maxLength: 3 }}
                        />
                      )}
                    />
                  </div>

                  {!sinComision && (
                    <div className="mb-24">
                      <Controller
                        name="commission"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Comisión (%)"
                            type="number"
                            variant="outlined"
                            disabled={loading}
                            error={!!errors.commission}
                            helperText={
                              errors?.commission?.message ||
                              'Ingrese el porcentaje de comisión para esta área'
                            }
                            required
                            fullWidth
                            inputProps={{
                              min: 0.1,
                              max: 100,
                              step: 0.1,
                            }}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Validar que no sea mayor a 100
                              if (value === '' || (parseFloat(value) >= 0.1 && parseFloat(value) <= 100)) {
                                field.onChange(value);
                              }
                            }}
                          />
                        )}
                      />
                    </div>
                  )}

                  <div className="mb-24">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sinComision}
                          onChange={(e) => setSinComision(e.target.checked)}
                        />
                      }
                      label="Sin comisión"
                    />
                  </div>

                  <div className="flex justify-center gap-16">
                    <Button
                      variant="outlined"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      onClick={resetForm}
                      disabled={loading}
                      size="large"
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      disabled={
                        !isEmpty(errors) || !allRequiredFieldsFilled || loading
                      }
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAreaDialog;
